import React, { useCallback } from 'react';
import useImportWorkOrderFileMutation from '../../hooks/useImportWorkOrderFileMutation';
import { ImportFileOutputData, Tenant } from '#/graphql/types';
import { unknownMappingValue } from '#/common/components/ColumnMapper/ColumnMapper';
import type { ObjectImportConfig } from '../asset/SubmitModal';
import SubmitConfirmModal from '../modals/SubmitConfirmModal';
import { buildAssetLookupImportConfig, buildTenantImportConfig } from '../base/commonImportConfigs';

interface WorkOrderSubmitModalProps {
  visible: boolean;
  uploadedFile: File | null;
  mapping: Record<string, string>;
  uniqueField: string;
  selectedTenant?: Tenant;
  onHiding: () => void;
  onImportStart: () => void;
  onSuccess: (importResult: ImportFileOutputData) => void;
  onFailed: (error: unknown) => void;
}

const WorkOrderSubmitModal: React.FC<WorkOrderSubmitModalProps> = ({
  visible,
  uploadedFile,
  mapping,
  uniqueField,
  selectedTenant,
  onHiding,
  onImportStart,
  onSuccess,
  onFailed,
}) => {
  const { importWorkOrderFile } = useImportWorkOrderFileMutation();

  // Generate the import config based on the mapping
  const generateImportConfig = useCallback((): string => {
    if (!selectedTenant || !mapping || Object.keys(mapping).length === 0) {
      return '';
    }

    const isColumnMapped = (field: string) =>
      !!mapping[field] && mapping[field] !== unknownMappingValue.key;

    const tenantImportConfig = buildTenantImportConfig(selectedTenant);
    const assetImportConfig = buildAssetLookupImportConfig(uniqueField, mapping);

    const userImportConfig: ObjectImportConfig = {
      model: 'UserModel',
      thirdPartyId: 'email',
      columnMap: {
        email: isColumnMapped('assigned_to_email') ? mapping['assigned_to_email'] : undefined,
      },
      onlyForMapping: true,
    };

    const workOrderImportConfig: ObjectImportConfig = {
      model: 'WorkOrderModel',
      thirdPartyId: 'thirdPartyId',
      thirdPartyIdSource: 'thirdPartySource',
      columnMap: {
        thirdPartyId: mapping['third_party_id'],
        thirdPartySource: { defaultValue: 'csv' },
        title: mapping['title'],
        category: mapping['category'],
        description: mapping['description'],
        state: { defaultValue: 'open' },
        // Optional fields
        type: isColumnMapped('type') ? mapping['type'] : undefined,
        assignedToId: isColumnMapped('assigned_to_email')
          ? {
              relatedTo: 'UserModel',
              defaultValue: null,
            }
          : undefined,
        priority: isColumnMapped('priority') ? mapping['priority'] : undefined,
        dueDate: isColumnMapped('due_date') ? mapping['due_date'] : undefined,
        // Related entities
        tenantId: {
          relatedTo: 'TenantModel',
        },
        assetId: {
          relatedTo: 'AssetModel',
        },
      },
      updateColumns: [
        'title',
        'type',
        'assignedToId',
        'description',
        'priority',
        'category',
        'dueDate',
        'assetId',
      ],
      uniqueForTenant: true,
    };

    const config: ObjectImportConfig[] = [
      tenantImportConfig,
      assetImportConfig,
      userImportConfig,
      workOrderImportConfig,
    ];

    return JSON.stringify(config);
  }, [uniqueField, mapping, selectedTenant]);

  return (
    <SubmitConfirmModal
      visible={visible}
      uploadedFile={uploadedFile}
      selectedTenant={selectedTenant}
      entityLabelPlural="Work Orders"
      entityLabelSingularLower="work order"
      generateImportConfig={generateImportConfig}
      importFile={importWorkOrderFile}
      onHiding={onHiding}
      onImportStart={onImportStart}
      onSuccess={onSuccess}
      onFailed={onFailed}
    />
  );
};

export default WorkOrderSubmitModal;
